import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FieldError, Input, Label } from '@/components/ui/input';
import { errorMessage } from '@/api/problem';

function makeSchema(t: (k: string) => string) {
  return z.object({
    email: z.string().email(t('auth.invalidEmail')),
    password: z.string().min(1, t('auth.passwordRequired')),
    totp: z
      .string()
      .regex(/^(\d{6})?$/, t('auth.totpFormat'))
      .optional(),
  });
}

type LoginForm = z.infer<ReturnType<typeof makeSchema>>;

export function LoginPage() {
  const { t } = useTranslation();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const form = useForm<LoginForm>({ resolver: zodResolver(makeSchema(t)), defaultValues: { email: '', password: '', totp: '' } });

  if (isAuthenticated) return <Navigate to={from} replace />;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await login({ email: values.email, password: values.password, totp: values.totp || undefined });
      void navigate(from, { replace: true });
    } catch (err) {
      form.setError('root', { message: errorMessage(err) });
    }
  });

  const { errors, isSubmitting } = form.formState;

  return (
    <div className="flex min-h-full items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-5 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-6" />
          <div>
            <div className="text-sm font-semibold">{t('app.name')}</div>
            <div className="text-xs text-muted-foreground">{t('app.tagline')}</div>
          </div>
        </div>
        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-3" noValidate>
          <div className="flex flex-col gap-1">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <Input id="email" type="email" autoComplete="username" aria-invalid={!!errors.email} {...form.register('email')} />
            <FieldError message={errors.email?.message} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <Input id="password" type="password" autoComplete="current-password" aria-invalid={!!errors.password} {...form.register('password')} />
            <FieldError message={errors.password?.message} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="totp">{t('auth.totp')}</Label>
            <Input id="totp" inputMode="numeric" autoComplete="one-time-code" aria-invalid={!!errors.totp} {...form.register('totp')} />
            <FieldError message={errors.totp?.message} />
          </div>
          {errors.root?.message && (
            <p role="alert" className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
              {errors.root.message}
            </p>
          )}
          <Button type="submit" disabled={isSubmitting} className="mt-1">
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
